import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, THEMES } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { User, LogOut, LayoutDashboard, Sparkles, FolderOpen, UserCircle, ShieldCheck, Store, Moon, Sun } from 'lucide-react';

export const Header = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const { theme, toggleTheme, isGothic } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="header">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <span className="text-xl font-cinzel font-bold tracking-wider text-[#F4EFE6]">
              Funtz<span className="text-[#D4AF37]">Guntz</span>AI
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/generator"
              className={`text-sm font-cinzel font-medium tracking-wider transition-colors ${
                isActive('/generator') ? 'text-[#D4AF37]' : 'text-[#C2B49A] hover:text-[#F4EFE6]'
              }`}
              data-testid="nav-generator"
            >
              Create
            </Link>
            <Link
              to="/shop"
              className={`text-sm font-cinzel font-medium tracking-wider transition-colors ${
                isActive('/shop') ? 'text-[#D4AF37]' : 'text-[#C2B49A] hover:text-[#F4EFE6]'
              }`}
              data-testid="nav-shop"
            >
              Shop
            </Link>
            {isAuthenticated && (
              <Link
                to="/dashboard"
                className={`text-sm font-cinzel font-medium tracking-wider transition-colors ${
                  isActive('/dashboard') ? 'text-[#D4AF37]' : 'text-[#C2B49A] hover:text-[#F4EFE6]'
                }`}
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
            )}
          </nav>

          {/* Theme Toggle & Auth Section */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg transition-all duration-300 hover:bg-[var(--bg-velvet)] border border-transparent hover:border-[var(--border-gold)]"
              data-testid="theme-toggle"
              aria-label={isGothic ? 'Switch to Manuscript mode' : 'Switch to Gothic mode'}
              title={isGothic ? 'Switch to Manuscript mode' : 'Switch to Gothic mode'}
            >
              {isGothic ? (
                <Sun className="w-5 h-5 text-[var(--gold)] transition-transform hover:rotate-12" />
              ) : (
                <Moon className="w-5 h-5 text-[var(--gold)] transition-transform hover:-rotate-12" />
              )}
            </button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 text-[#C2B49A] hover:text-[#F4EFE6] hover:bg-[#220F13] rounded-lg"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#220F13] border border-[#D4AF37]/30 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <span className="text-sm font-cinzel font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-48 bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg shadow-2xl"
                >
                  <DropdownMenuItem 
                    onClick={() => navigate('/generator')}
                    className="cursor-pointer text-[#C2B49A] hover:text-[#F4EFE6] focus:text-[#F4EFE6] focus:bg-[#220F13] rounded-lg font-serif"
                    data-testid="menu-create"
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    Create Font
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard')}
                    className="cursor-pointer text-[#C2B49A] hover:text-[#F4EFE6] focus:text-[#F4EFE6] focus:bg-[#220F13] rounded-lg font-serif"
                    data-testid="menu-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/collections')}
                    className="cursor-pointer text-[#C2B49A] hover:text-[#F4EFE6] focus:text-[#F4EFE6] focus:bg-[#220F13] rounded-lg font-serif"
                    data-testid="menu-collections"
                  >
                    <FolderOpen className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    Collections
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/profile')}
                    className="cursor-pointer text-[#C2B49A] hover:text-[#F4EFE6] focus:text-[#F4EFE6] focus:bg-[#220F13] rounded-lg font-serif"
                    data-testid="menu-profile"
                  >
                    <UserCircle className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/shop')}
                    className="cursor-pointer text-[#C2B49A] hover:text-[#F4EFE6] focus:text-[#F4EFE6] focus:bg-[#220F13] rounded-lg font-serif"
                    data-testid="menu-shop"
                  >
                    <Store className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    Shop
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin')}
                      className="cursor-pointer text-[#D4AF37] hover:text-[#F4EFE6] focus:text-[#F4EFE6] focus:bg-[#722F37]/20 rounded-lg font-serif"
                      data-testid="menu-admin"
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#D4AF37]/20" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer text-[#9E2A2B] hover:text-[#FF6B6B] focus:text-[#FF6B6B] focus:bg-[#9E2A2B]/10 rounded-lg font-serif"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="text-[#C2B49A] hover:text-[#F4EFE6] hover:bg-[#220F13] text-sm font-cinzel font-medium tracking-wider rounded-lg"
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    className="btn-primary rounded-lg"
                    data-testid="register-btn"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
